"use client";

import { useState } from "react";
import { fillSlots } from "@/components/tools/fill-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  ALPHABET,
  brailleToText,
  CAPITAL_INDICATOR,
  DIGIT_TO_LETTER,
  DIGITS,
  LETTER_TO_BRAILLE,
  NUMBER_INDICATOR,
  textToBraille,
} from "@/lib/tool-kit/braille/ueb";

type Direction = "text-to-braille" | "braille-to-text";

type Strings = { [K in keyof typeof BRAILLE_TRANSLATOR_STRINGS]: string };

const DIRECTIONS: { value: Direction; label: keyof Strings }[] = [
  { value: "text-to-braille", label: "directionTextToBraille" },
  { value: "braille-to-text", label: "directionBrailleToText" },
];

/** English strings. A translated content file overrides these through `ui`. */
export const BRAILLE_TRANSLATOR_STRINGS = {
  direction: "Direction",
  text: "Text",
  braille: "Braille",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  brailleAlphabetNumbersChart: "Braille Alphabet & Numbers Chart",
  chartNote:
    "Numbers reuse the letters a-j after the number indicator {numberIndicator}, and the capital indicator {capitalIndicator} marks the following letter as uppercase.",
  directionTextToBraille: "Text → Braille",
  directionBrailleToText: "Braille → Text",
  textPlaceholder: "Hello world",
  aNoteOnGrade1: "A Note on Grade 1 vs Grade 2 Braille",
  thisToolProducesGrade1:
    "This tool produces Grade 1 (uncontracted) braille, where every letter is written out cell by cell. Most real-world braille — books, signage, official documents — is Grade 2 (contracted), which uses around 180 contractions and shorthand signs to save space and speed up reading.",
  treatThisTranslatorAsAn:
    "Treat this translator as an educational aid for learning the braille alphabet, not a substitute for professional transcription. Material intended for blind readers should be prepared by a certified braille transcriber.",
} as const;

export default function BrailleTranslatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("text-to-braille");
  const [copied, setCopied] = useState(false);

  const output =
    direction === "text-to-braille"
      ? textToBraille(input)
      : brailleToText(input);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  const strings = useToolUi(BRAILLE_TRANSLATOR_STRINGS);

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="braille-direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {strings[d.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="braille-input"
        >
          {direction === "text-to-braille" ? strings.text : strings.braille}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="braille-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={
            direction === "text-to-braille"
              ? strings.textPlaceholder
              : "\u2820\u2813\u2811\u2807\u2807\u2815 \u283A\u2815\u2817\u2807\u2819"
          }
          value={input}
        />
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="braille-output"
          >
            {direction === "text-to-braille" ? strings.braille : strings.text}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="braille-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Chart */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.brailleAlphabetNumbersChart}
        </h2>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
          {ALPHABET.map((letter) => (
            <div
              className="rounded-md border border-border bg-background p-2 text-center"
              key={letter}
            >
              <p className="text-2xl text-foreground">
                {LETTER_TO_BRAILLE[letter]}
              </p>
              <p className="mt-1 text-muted-foreground text-xs uppercase">
                {letter}
              </p>
            </div>
          ))}
          {DIGITS.map((digit) => (
            <div
              className="rounded-md border border-border bg-background p-2 text-center"
              key={digit}
            >
              <p className="text-2xl text-foreground">
                {NUMBER_INDICATOR}
                {LETTER_TO_BRAILLE[DIGIT_TO_LETTER[digit]]}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">{digit}</p>
            </div>
          ))}
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {fillSlots(strings.chartNote, {
            numberIndicator: (
              <span className="text-lg">{NUMBER_INDICATOR}</span>
            ),
            capitalIndicator: (
              <span className="text-lg">{CAPITAL_INDICATOR}</span>
            ),
          })}
        </p>
      </section>
      {/* Honest notes */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.aNoteOnGrade1}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.thisToolProducesGrade1}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.treatThisTranslatorAsAn}
        </p>
      </section>
    </>
  );
}
